import type {Notification} from '~/src/models/notification'
import {isESignNotification, isTimeOffNotification} from '~/src/models/notification'

/**
 * A candidate's feed can hold notifications that are not theirs to act on, so the bell shows them
 * only the candidate-relevant ones, and the inbox (/notifications) shows the same set. Shared here so
 * the two cannot list or count different notifications.
 */

/** How many unread notifications the candidate's badge reads (and the inbox's Unread figure). */
export const MAX_CANDIDATE_UNREAD_COUNT = 20

/** The candidate's unread list: one query, so the bell and the inbox share its cache entry. */
export const CANDIDATE_UNREAD_QUERY = {page: 1, limit: MAX_CANDIDATE_UNREAD_COUNT, isRead: false}

function getStringField(source: unknown, key: string): string | undefined {
  if (!source || typeof source !== 'object') return undefined
  const value = (source as Record<string, unknown>)[key]
  return typeof value === 'string' && value ? value : undefined
}

/** Every id the signed-in candidate may appear under in a notification's metadata.applicantId. */
export function getCandidateUserIds(currentUser: unknown): Set<string> {
  return new Set(
    [
      getStringField(currentUser, 'id'),
      getStringField(currentUser, '_id'),
      getStringField(currentUser, 'applicantId'),
      getStringField(currentUser, 'applicantID'),
      getStringField(currentUser, 'candidateId'),
    ].filter((value): value is string => Boolean(value)),
  )
}

/** eSign or time-off, and about this candidate when it names one. */
export function isCandidateRelevantNotification(
  notification: Notification,
  candidateUserIds: Set<string>,
): boolean {
  if (!isESignNotification(notification.type) && !isTimeOffNotification(notification.type)) {
    return false
  }

  const applicantId = notification.metadata?.applicantId
  if (!applicantId || candidateUserIds.size === 0) return true

  return candidateUserIds.has(applicantId)
}
